import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckSquare, Pencil, Send, SmilePlus, Trash2 } from 'lucide-react';
import { chatAPI, tasksAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import useSocket from '../../hooks/useSocket';
import { Avatar, btnGhost, fmtDateTime, inputCls } from '../../components/ui';

const QUICK_EMOJI = ['👍', '❤️', '🎉', '👀'];

function Message({ msg, mine, isAdmin, groups, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(msg.body_rt);
  const [showConvert, setShowConvert] = useState(false);

  const saveEdit = async () => {
    if (text.trim() && text !== msg.body_rt) {
      await chatAPI.edit(msg.id, { body_rt: text.trim() });
      await onChanged();
    }
    setEditing(false);
  };

  const react = async (emoji) => {
    await chatAPI.react(msg.id, emoji);
    await onChanged();
  };

  const convert = async (groupId) => {
    await chatAPI.convert(msg.id, groupId);
    setShowConvert(false);
    await onChanged();
  };

  return (
    <li className="group flex items-start gap-2.5">
      <Avatar name={msg.author_name} size={26} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold">{msg.author_name}</span>
          <span className="text-[10px] text-pm-faint">{fmtDateTime(msg.created_at)}</span>
          {msg.edited_at && <span className="text-[10px] text-pm-faint">(edited)</span>}
          <span className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100">
            {QUICK_EMOJI.map((e) => (
              <button key={e} type="button" onClick={() => react(e)} className="rounded p-0.5 text-xs hover:bg-pm-raised" title={`React ${e}`}>
                {e}
              </button>
            ))}
            {!msg.converted_to && (
              <button type="button" onClick={() => setShowConvert(!showConvert)} className="rounded p-1 text-pm-faint hover:bg-pm-raised hover:text-pm-text" title="Convert to task">
                <CheckSquare size={13} />
              </button>
            )}
            {(mine || isAdmin) && (
              <>
                <button type="button" onClick={() => setEditing(true)} className="rounded p-1 text-pm-faint hover:bg-pm-raised hover:text-pm-text" title="Edit">
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await chatAPI.remove(msg.id);
                    await onChanged();
                  }}
                  className="rounded p-1 text-pm-faint hover:text-pm-danger"
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </span>
        </div>
        {editing ? (
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur();
              if (e.key === 'Escape') setEditing(false);
            }}
            autoFocus
            className={`${inputCls} mt-0.5 py-1 text-sm`}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm">{msg.body_rt}</p>
        )}
        <div className="mt-0.5 flex items-center gap-1.5">
          {Object.entries(msg.reactions || {})
            .filter(([, mids]) => mids.length)
            .map(([emoji, mids]) => (
              <button key={emoji} type="button" onClick={() => react(emoji)} className="rounded-full bg-pm-raised px-1.5 py-0.5 text-[11px]">
                {emoji} {mids.length}
              </button>
            ))}
          {msg.converted_to?.type === 'task' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-pm-raised px-2 py-0.5 text-[10px] text-pm-accent">
              <CheckSquare size={10} /> converted to task
            </span>
          )}
        </div>
        {showConvert && (
          <div className="mt-1 flex items-center gap-1.5 text-xs">
            <span className="text-pm-faint">Create task in:</span>
            {groups.map((g) => (
              <button key={g.id} type="button" onClick={() => convert(g.id)} className="rounded-full border border-pm-border px-2 py-0.5 hover:border-pm-accent hover:text-pm-accent">
                {g.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

export default function DiscussionScreen() {
  const { project } = useProject();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [text, setText] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const bottomRef = useRef(null);
  const user = getUser();

  const load = useCallback(async () => {
    const [{ data: ch }, { data: g }] = await Promise.all([chatAPI.channels(), tasksAPI.groups(project.id)]);
    const mine = ch.channels.find((c) => c.project_id === project.id);
    setChannel(mine);
    setGroups(g.groups);
    if (mine) {
      const { data } = await chatAPI.messages(mine.id);
      setMessages(data.messages);
      setHasMore(data.messages.length >= 50);
      await chatAPI.markRead(mine.id);
    }
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const onEvent = useCallback(
    (ev) => {
      if (ev.type === 'chat.message' && ev.project_id === project.id) {
        setMessages((prev) => (prev.some((m) => m.id === ev.message.id) ? prev : [...prev, ev.message]));
        if (channel) chatAPI.markRead(channel.id);
      }
      if (ev.type === 'chat.deleted') {
        setMessages((prev) => prev.filter((m) => m.id !== ev.message_id));
      }
    },
    [project.id, channel]
  );
  useSocket(onEvent);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !channel) return;
    const { data } = await chatAPI.send(channel.id, { body_rt: text.trim() });
    setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
    setText('');
  };

  const loadOlder = async () => {
    if (!channel || !messages.length) return;
    const { data } = await chatAPI.messages(channel.id, messages[0].created_at);
    setMessages((prev) => [...data.messages, ...prev]);
    setHasMore(data.messages.length >= 50);
  };

  const refresh = useCallback(async () => {
    if (!channel) return;
    const { data } = await chatAPI.messages(channel.id);
    setMessages(data.messages);
  }, [channel]);

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {hasMore && (
          <button type="button" onClick={loadOlder} className={`${btnGhost} mb-3 w-full py-1 text-xs`}>
            Load older messages
          </button>
        )}
        <ul className="space-y-4 pb-3">
          {messages.map((m) => (
            <Message
              key={m.id}
              msg={m}
              mine={m.author_mid === (user?.mid || '')}
              isAdmin={user?.role === 'admin'}
              groups={groups}
              onChanged={refresh}
            />
          ))}
          {messages.length === 0 && (
            <li className="py-16 text-center text-sm text-pm-muted">No messages yet — start the discussion.</li>
          )}
        </ul>
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="mt-2 flex gap-2 border-t border-pm-border pt-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Message #${project.name} — @name to mention`}
          className={inputCls}
        />
        <button type="submit" disabled={!text.trim()} className={`${btnGhost} px-3`} title="Send">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}
